import { action, Action, computed, Computed, thunk, Thunk } from 'easy-peasy';
import { Project, IssueChatPayload, Message, SaveChatUserPayload, SaveChatUserResponse, SearchUser } from '~/types';
import { StoreModel } from '~/store';
import axios from '~/axiosWrapper';
import { API } from '~/app/routes';
import { CommonModel, getCommonModel } from '~/store/commonStore';
import { projectRepository } from '~/data/repositories';
import { IssueOrServiceType, MessageType } from '~/app/data/statuses/chat';

export interface ProjectChatDetailApi {
    id: number;
    number: string;
    name: string;
    startDate: string | null;
    endDate: string | null;
    standardPriceType: string | null;
    workingDays: string | null;
    status: boolean | number | null;
    created_at?: string;
    updated_at?: string;
}

interface ChatHistoryApiRow {
    issue_id?: string | number;
    senderId?: string | number;
    senderName?: string;
    message?: string;
    dateTime?: string;
    actions?: number | null;
    attachmentUrls?: string[];
}

function mapHistoryRow(row: ChatHistoryApiRow, projectId: string): Message {
    const raw = row.dateTime || '';
    const dt = raw ? new Date(raw.replace(' ', 'T')) : null;
    const time =
        dt && !Number.isNaN(dt.getTime())
            ? `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`
            : '';

    return {
        user: String(row.senderId ?? ''),
        senderId: String(row.senderId ?? ''),
        senderName: String(row.senderName ?? ''),
        issueId: String(row.issue_id ?? projectId),
        message: String(row.message ?? ''),
        type: MessageType.PROJECT,
        dateTime: raw,
        time,
        actions: row.actions ?? null,
        attachmentUrls: Array.isArray(row.attachmentUrls) ? row.attachmentUrls : [],
        showDate: false,
    };
}

function buildChatMessagesFromHistoryRows(rows: ChatHistoryApiRow[], projectId: string): Message[] {
    let previousDate = '';
    return rows.map((row) => {
        const m = mapHistoryRow(row, projectId);
        const day = (row.dateTime || '').slice(0, 10);
        m.showDate = previousDate === '' || day !== previousDate;
        previousDate = day;
        return m;
    });
}

function extractHistoryRows(histRes: unknown): ChatHistoryApiRow[] {
    if (!histRes || typeof histRes !== 'object' || !('data' in histRes)) {
        return [];
    }
    const data = (histRes as { data?: unknown }).data;
    return Array.isArray(data) ? (data as ChatHistoryApiRow[]) : [];
}

function mapChatUser(row: Record<string, unknown>): SearchUser {
    return {
        id: String(row.id ?? ''),
        name: String(row.name ?? ''),
        email: String(row.email ?? ''),
        phone: String(row.phone ?? ''),
        avatar: String(row.avatar ?? ''),
        added: row.added !== undefined ? Boolean(row.added) : true,
        deleted: false,
        new: false,
    };
}

export interface ProjectModel extends CommonModel<Project> {
    data: Project;
    fetchProject: Thunk<ProjectModel, Project['id'], any, StoreModel, Promise<void>>;
    fetchedProject: Action<ProjectModel, Project>;

    projectChatLoading: boolean;
    setProjectChatLoading: Action<ProjectModel, boolean>;
    isReadOnlyChat: Computed<ProjectModel, boolean, StoreModel>;
    projectChatDetail: ProjectChatDetailApi | null;
    setProjectChatDetail: Action<ProjectModel, ProjectChatDetailApi | null>;
    syncProjectChatId: Action<ProjectModel, string>;
    setChatMessages: Action<ProjectModel, Message[]>;
    tempChatUsers: SearchUser[];
    setTempChatUsers: Action<ProjectModel, SearchUser[]>;
    setChatUsers: Action<ProjectModel, SearchUser[]>;
    fetchChatUsers: Thunk<ProjectModel, string, any, StoreModel, Promise<void>>;
    saveChatUser: Thunk<ProjectModel, SaveChatUserPayload, any, StoreModel, Promise<SaveChatUserResponse>>;
    addChatUser: Thunk<ProjectModel, IssueChatPayload, any, StoreModel, Promise<SaveChatUserResponse>>;
    addChatMessage: Action<ProjectModel, Message>;
    resetProjectChat: Action<ProjectModel, void>;
    loadProjectChat: Thunk<ProjectModel, string, any, StoreModel, Promise<void>>;
    refreshProjectChatMessages: Thunk<ProjectModel, string, any, StoreModel, Promise<void>>;
}

export const projectModel: ProjectModel = {
    ...getCommonModel<Project>(API.projects),
    data: {
        id: '',
        name: '',
        number: '',
        image: '',
        chat: [],
        chatUsers: [],
    },

    projectChatLoading: false,
    setProjectChatLoading: action((state, payload) => {
        state.projectChatLoading = payload;
    }),
    isReadOnlyChat: computed(
        [(s) => s.projectChatDetail],
        (detail) => detail != null && !detail.status,
    ),
    projectChatDetail: null,
    setProjectChatDetail: action((state, payload) => {
        state.projectChatDetail = payload;
    }),
    syncProjectChatId: action((state, projectId) => {
        state.data.id = String(projectId);
    }),
    setChatMessages: action((state, payload) => {
        state.data.chat = payload;
    }),
    tempChatUsers: [],
    setTempChatUsers: action((state, payload) => {
        state.tempChatUsers = payload;
    }),
    setChatUsers: action((state, payload) => {
        state.data.chatUsers = payload;
        state.tempChatUsers = payload;
    }),
    fetchChatUsers: thunk(async (actions, projectId) => {
        const id = String(projectId ?? '').trim();
        if (!id) {
            return;
        }
        const raw = await axios.post<never, Record<string, unknown>[]>(`${API.projectUsers}/fetch/${id}`);
        const rows = Array.isArray(raw) ? raw : [];
        actions.setChatUsers(rows.map(mapChatUser));
    }),
    saveChatUser: thunk((actions, payload) => {
        return axios
            .post<SaveChatUserPayload, SaveChatUserResponse>(`${API.projectUsers}/save`, payload)
            .then((data) => data);
    }),
    addChatUser: thunk((actions, payload) => {
        return axios.post<never, SaveChatUserResponse>(`${API.projectUsers}/add`, payload).then((data) => data);
    }),
    addChatMessage: action((state, payload) => {
        if (!state.data.id || String(payload.issueId) !== String(state.data.id)) {
            return;
        }
        const chat = state.data.chat ?? [];
        chat.push(payload);
        state.data.chat = chat;
    }),
    resetProjectChat: action((state) => {
        state.projectChatLoading = false;
        state.projectChatDetail = null;
        state.data.chat = [];
        state.data.chatUsers = [];
        state.tempChatUsers = [];
    }),
    loadProjectChat: thunk(async (actions, projectId) => {
        actions.resetProjectChat();
        actions.syncProjectChatId(projectId);
        actions.setProjectChatLoading(true);

        try {
            const detailRes = await axios.get<{ success?: boolean; data?: ProjectChatDetailApi }>(
                `${API.chatProjectDetail}/${projectId}`,
            );
            const detail = detailRes?.data;
            if (detail) {
                actions.setProjectChatDetail(detail as ProjectChatDetailApi);
            }

            try {
                await axios.post(API.addUserToChat, {
                    issue_id: Number(projectId),
                    personnel_id: '',
                    type: IssueOrServiceType.PROJECT,
                });
            } catch {
                // already added user in project chat
            }

            const histRes = await axios.post<{ success?: boolean; data?: ChatHistoryApiRow[] }>(API.getCurrentChatHistory, {
                issue_id: Number(projectId),
                type: IssueOrServiceType.PROJECT,
            });
            actions.setChatMessages(buildChatMessagesFromHistoryRows(extractHistoryRows(histRes), projectId));

            await actions.fetchChatUsers(projectId);
        } finally {
            actions.setProjectChatLoading(false);
        }
    }),
    refreshProjectChatMessages: thunk(async (actions, projectId) => {
        if (!projectId) return;
        const histRes = await axios.post<{ success?: boolean; data?: ChatHistoryApiRow[] }>(API.getCurrentChatHistory, {
            issue_id: Number(projectId),
            type: IssueOrServiceType.PROJECT,
        });
        actions.setChatMessages(buildChatMessagesFromHistoryRows(extractHistoryRows(histRes), projectId));
    }),

    fetchProject: thunk(async (actions, payload) => {
        const data = await projectRepository.getProject(payload);
        data && actions.fetchedProject(data);
    }),
    fetchedProject: action((state, payload) => {
        state.data = {
            ...state.data,
            ...payload,
            chat: payload.chat ?? state.data.chat ?? [],
            chatUsers: payload.chatUsers ?? state.data.chatUsers ?? [],
        };
    }),
};
